/**
 * useAvailabilityDraft (BON-241) — local draft state for the teacher
 * Availability surface. Keeps the staged rhythm + exceptions out of the
 * page component (which otherwise blows the lint size caps) and gives the
 * dirty check one home.
 */
import { useEffect, useRef, useState } from 'react';

import type { AvailabilitySlot, ExceptionEntry, OwnAvailability } from '../../../api/availability';

import { futureExceptions, isDirty, rhythmToDraft, type RhythmDraft } from './availabilityModel';

export function useAvailabilityDraft(saved: OwnAvailability | undefined) {
  const [rhythm, setRhythm] = useState<RhythmDraft>({});
  const [exceptions, setExceptions] = useState<ExceptionEntry[]>([]);
  const [justSaved, setJustSaved] = useState(false);

  // Re-seed when the server copy changes — initial load and after a save.
  // Guarded on an explicit "user has edited" flag, NOT on `dirty`: React
  // Query refetches on window focus, so without a guard an alt-tab (after,
  // say, saving in another tab) silently overwrites unsaved edits. It has to
  // be `touched` rather than `dirty` because an unseeded empty draft already
  // compares dirty against saved data, which would block the initial seed.
  const touchedRef = useRef(false);
  useEffect(() => {
    if (!saved || touchedRef.current) return;
    setRhythm(rhythmToDraft(saved.rhythm));
    setExceptions(futureExceptions(saved.exceptions));
  }, [saved]);

  /** Mark the draft user-owned so a background refetch can't clobber it. */
  function touch() {
    touchedRef.current = true;
    setJustSaved(false);
  }

  function setSlot(weekday: number, slot: AvailabilitySlot) {
    touch();
    setRhythm((prev) => ({ ...prev, [weekday]: slot }));
  }

  function addException(entry: ExceptionEntry) {
    touch();
    setExceptions((prev) => [...prev, entry].sort((a, b) => a.date.localeCompare(b.date)));
  }

  function removeException(date: string) {
    touch();
    setExceptions((prev) => prev.filter((e) => e.date !== date));
  }

  const dirty = saved
    ? isDirty({ rhythm: saved.rhythm, exceptions: saved.exceptions }, { rhythm, exceptions })
    : false;

  return {
    rhythm,
    /** Re-filtered at read time so a tab left open across the UTC rollover
     *  can't submit a now-past date and lose the whole save to a 400. */
    exceptions: futureExceptions(exceptions),
    dirty,
    justSaved,
    markSaved: () => {
      // The save response re-seeds the draft, so release user ownership.
      touchedRef.current = false;
      setJustSaved(true);
    },
    setSlot,
    addException,
    removeException,
  };
}
